import { useCallback } from 'react';

import {
  SuppliersPageOrderAgentSettingDocument,
  useSuppliersPageOrderAgentSettingQuery,
  useSuppliersPageOrderAgentSettingUpdateMutation,
} from '../../../../../../../generated';

const LOAD_ERROR_MESSAGE = 'Could not load the Order Agent setting.';
const SAVE_ERROR_MESSAGE = 'Could not change the Order Agent setting.';

export type OrderAgentSaveResult =
  | { kind: 'success' }
  | { kind: 'error'; message: string };

interface UseOrderAgentSettingResult {
  isEnabled: boolean;
  isLoading: boolean;
  errorMessage?: string;
  setEnabled: (enabled: boolean) => Promise<OrderAgentSaveResult>;
  isSaving: boolean;
}

/**
 * Reads and writes one distributor's Order Agent switch. The failure members of
 * the mutation's union come back as `{ kind: 'error' }` so the tab can show the
 * server's message; the query is refetched after a write so the switch always
 * shows the stored value rather than the one that was requested.
 */
export const useOrderAgentSetting = (
  supplierId?: string
): UseOrderAgentSettingResult => {
  const { data, loading, error } = useSuppliersPageOrderAgentSettingQuery({
    variables: { id: supplierId ?? '' },
    skip: !supplierId,
    fetchPolicy: 'cache-and-network',
  });

  const [mutate, { loading: isSaving }] =
    useSuppliersPageOrderAgentSettingUpdateMutation({
      refetchQueries: [SuppliersPageOrderAgentSettingDocument],
      awaitRefetchQueries: true,
    });

  const setEnabled = useCallback(
    async (enabled: boolean): Promise<OrderAgentSaveResult> => {
      if (!supplierId) {
        return { kind: 'error', message: SAVE_ERROR_MESSAGE };
      }

      try {
        const response = await mutate({
          variables: { input: { supplierId, isOrderAgentEnabled: enabled } },
        });
        const result = response.data?.supplierSetting;

        if (result?.__typename === 'SupplierSetting') {
          return { kind: 'success' };
        }

        // Mutations resolve on GraphQL errors (errorPolicy 'all'), so a
        // rejected call arrives here with no payload rather than as a throw.
        const [firstError] = response.errors ?? [];
        const serverMessage =
          result && 'message' in result ? result.message : undefined;
        return {
          kind: 'error',
          message: serverMessage ?? firstError?.message ?? SAVE_ERROR_MESSAGE,
        };
      } catch (caught) {
        return {
          kind: 'error',
          message:
            caught instanceof Error ? caught.message : SAVE_ERROR_MESSAGE,
        };
      }
    },
    [mutate, supplierId]
  );

  return {
    isEnabled: Boolean(
      data?.supplier?.chocoAiSupplierSetting?.isOrderAgentEnabled
    ),
    isLoading: loading && !data,
    errorMessage: error ? LOAD_ERROR_MESSAGE : undefined,
    setEnabled,
    isSaving,
  };
};
